<template>
    <div style="text-align: center;">
        <BriefcaseIcon style="width: 60px;" />
        <h1 class="p-text-center">
            {{ t("registration.tech.headline7") }}
        </h1>
        <Timeline :step="step" />
    </div>
    <div>
        <span class="p-float-label">
            <MultiSelect data-cy="tech-reg-employertype"
                         v-model="v$.desiredEmployerTypes.$model"
                         id="remote"
                         :class="{ 'p-invalid': v$.desiredEmployerTypes.$error }"
                         :options="employerTypes"
                         optionLabel="value"
                         optionValue="type" />
            <label>{{t("registration.tech.employertype")}}</label>
        </span>
        <small class="p-error"
               v-for="(error, index) of v$.desiredEmployerTypes.$errors"
               :key="index">
            {{ t(error.$message) }}
        </small>
    </div>
    <div class="p-mt-4">
        <span class="p-float-label">
            <MultiSelect data-cy="tech-reg-remote"
                         v-model="v$.remote.$model"
                         id="remote"
                         :class="{ 'p-invalid': v$.remote.$error }"
                         :options="remoteOptions"
                         optionLabel="name"
                         optionValue="value" />
            <label>{{t("registration.tech.remote")}}</label>
        </span>
        <small class="p-error"
               v-for="(error, index) of v$.remote.$errors"
               :key="index">
            {{ t(error.$message) }}
        </small>
    </div>
    <div v-if="!computedIsRemoteOnly">
        <span data-cy="tech-reg-search-radius" class="p-float-label">
            <h5>
                {{t("job.preferences.choose.commute")}}
            </h5>
            <Slider v-model="v$.searchRadius.$model"
                    :step="1"
                    :min="1"
                    :max="100" />
        </span>
    </div>
    <div class="p-mt-4">
        <span class="p-no-float-label relocate">
            <h5>
                {{t("job.preferences.choose.move.to.which.city")}}
            </h5>
            <ul class="tags" data-cy="profile-edit-relocate-options"
                v-if="computedRelocations && computedRelocations.length > 0">
                <li v-for="option in computedRelocations" :key="option.name">
                    {{option.name}}
                    <Button icon="pi pi-times"
                            class="p-button-rounded p-button-outlined"
                            data-cy="tech-reg-relocate-option"
                            @click="handleRelocationOptionRemoval($event, option)" />
                </li>
            </ul>
            <Dropdown v-model="selectedRelocationCity"
                      optionLabel="name"
                      @change="relocationCitySelected"
                      ref="relocationCityDropDown"
                      data-cy="tech-reg-relocate-dropdown"
                      :filter="true"
                      :autoFilterFocus="true"
                      filterIcon="pi pi-search"
                      :showToggleAll="false"
                      :class="{ 'p-invalid': !hasSelectedCityOrState && relocate }"
                      :options="relocationOptions" />
            <label>{{t("job.preferences.choose.move.to.which.city.add")}}</label>
        </span>
    </div>
    <div>
        <h5 class="p-mt-3">
            {{t("job.preferences.willingness.to.travel")}}
        </h5>
        <span class="p-float-label">
            <Dropdown data-cy="tech-reg-willingness-to-travel-dropdown"
                      :options="willingsnessToTravelOptions"
                      id="days_label"
                      :class="{ 'p-invalid': v$.willingnessToTravelDaysPerWeek.$error }"
                      v-model="v$.willingnessToTravelDaysPerWeek.$model"
                      :showClear="true"
                      @blur="daysChanged"
                      optionLabel="name"
                      optionValue="id" />
            <label class="p-pl-1">{{t("job.preferences.willingness.to.travel.label")}}</label>
        </span>
        <small class="p-error"
               v-for="(error, index) of v$.willingnessToTravelDaysPerWeek.$errors"
               :key="index">
            {{error.$message}}
        </small>
    </div>
    <div>
        <h5 class="p-mt-3">{{t("employer.preferences.choose.blacklist.company")}}</h5>
        <span data-cy="tech-reg-excluded-companies" class="p-float-label ">
            <ul class="tags"
                v-if="computedCompanies.length && computedCompanies.length > 0">
                <li v-for="company in computedCompanies" :key="company.id">
                    {{company.name}}
                    <Button icon="pi pi-times"
                            class="p-button-rounded p-button-outlined"
                            @click="handleCompanyExclusionRemoval($event, company)" />
                </li>
            </ul>
            <AutoComplete class="excluded-companies-autocomplete"
                          v-model="computedCompanies"
                          data-cy="wizard-excluded-companies"
                          :multiple="true"
                          :suggestions="filteredCompanies"
                          @complete="handleFilterInput"
                          field="name"
                          :placeholder="t('employer.preferences.search.companies')">
                <template #item="slotProps">
                    <div v-if="slotProps.item.id<0"
                         class="new-company">
                        {{t("employer.preferences.missing.company")}} <div class="p-button button-secondary">{{t("employer.preferences.missing.company.create")}}</div>
                    </div>
                    <div v-else>{{slotProps.item.name}}</div>
                </template>
            </AutoComplete>
        </span>
        <div class="exclude-information p-mt-1">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd"
                      d="M16.2008 9.00005C16.2008 10.9096 15.4422 12.741 14.092 14.0912C12.7417 15.4415 10.9103 16.2 9.00078 16.2C7.09122 16.2 5.25987 15.4415 3.90961 14.0912C2.55935 12.741 1.80078 10.9096 1.80078 9.00005C1.80078 7.09049 2.55935 5.25914 3.90961 3.90888C5.25987 2.55862 7.09122 1.80005 9.00078 1.80005C10.9103 1.80005 12.7417 2.55862 14.092 3.90888C15.4422 5.25914 16.2008 7.09049 16.2008 9.00005V9.00005ZM9.90078 5.40005C9.90078 5.63874 9.80596 5.86766 9.63718 6.03644C9.46839 6.20523 9.23948 6.30005 9.00078 6.30005C8.76209 6.30005 8.53317 6.20523 8.36439 6.03644C8.1956 5.86766 8.10078 5.63874 8.10078 5.40005C8.10078 5.16135 8.1956 4.93244 8.36439 4.76365C8.53317 4.59487 8.76209 4.50005 9.00078 4.50005C9.23948 4.50005 9.46839 4.59487 9.63718 4.76365C9.80596 4.93244 9.90078 5.16135 9.90078 5.40005ZM8.10078 8.10005C7.86209 8.10005 7.63317 8.19487 7.46439 8.36365C7.2956 8.53244 7.20078 8.76135 7.20078 9.00005C7.20078 9.23874 7.2956 9.46766 7.46439 9.63645C7.63317 9.80523 7.86209 9.90005 8.10078 9.90005V12.6C8.10078 12.8387 8.1956 13.0677 8.36439 13.2364C8.53317 13.4052 8.76209 13.5 9.00078 13.5H9.90078C10.1395 13.5 10.3684 13.4052 10.5372 13.2364C10.706 13.0677 10.8008 12.8387 10.8008 12.6C10.8008 12.3614 10.706 12.1324 10.5372 11.9637C10.3684 11.7949 10.1395 11.7 9.90078 11.7V9.00005C9.90078 8.76135 9.80596 8.53244 9.63718 8.36365C9.46839 8.19487 9.23948 8.10005 9.00078 8.10005H8.10078Z"
                      fill="#0ED3C0" />
            </svg>
            <p class="p-ml-1">{{t("employer.preferences.company.default.exclude")}}</p>
        </div>
    </div>



    <div class="call-to-action mobile-bottom-nav">
        <Button data-cy="tech-reg-button-back"
                class="button-secondary button-back p-pl-3"
                @click="prev()">
            <i class="pi pi-angle-left" />{{t("back")}}
        </Button>
        <Button data-cy="tech-reg-button-next"
                class="button-secondary button-next p-pr-3"
                @click="next()">
            {{t("continue")}}
            <i class="pi pi-angle-right" />
        </Button>
    </div>
</template>

<script lang="ts" setup>
    import { ref, onMounted, computed, getCurrentInstance, nextTick } from 'vue';
    import { useI18n } from "vue-i18n";
    import { HomeOffice } from "@/models/HomeOffice";
    import useVuelidate from '@vuelidate/core';
    import { BriefcaseIcon } from "@heroicons/vue/outline";
    import { ProfileSuggestions } from "@/models/ProfileSuggestions";
    import { createProfileService } from "@/services/profileService";
    import { getTechEmployerTypes } from "@/models/employerTypes";
    import { Company } from "@/models/Company";
    import Timeline from "@/registration/tech/wizard/Timeline.vue";

    const app = getCurrentInstance();
    const props = defineProps(["candidate", "step"]);
    const step = ref(props.step);
    const emit = defineEmits(['next', 'previous']);
    const user = ref(props.candidate);
    const { t } = useI18n();
    const suggestions = ref<ProfileSuggestions>();

    type RelocationOption = {
        id: number;
        name: string;
        isCity: boolean;
    };
    const remoteOptions = ref([
        { value: HomeOffice.No, name: t("job.preferences.no.remote") },
        { value: HomeOffice.Possible, name: t("job.preferences.remote.possible") },
        { value: HomeOffice.Mandatory, name: t("job.preferences.remote.required") }
    ]);

    const willingsnessToTravelOptions = [1, 2, 3, 4, 5].map(x => {
        if (x == 1)
            return { id: x, name: t('candidate.willingnesstotravel.dayperweek', { day: x.toString() }) }
        return { id: x, name: t('candidate.willingnesstotravel.daysperweek', { days: x.toString() }) }

    });

    const loadSuggestions = async () => {
        const service = await createProfileService(app);
        const result = await service.getSuggestions();
        if (result != null) {
            suggestions.value = result;
        }

        suggestions.value?.cities?.forEach((city) =>
            relocationOptions.value.push({ id: city.id, name: city.name, isCity: true })
        );

        suggestions.value?.federalStates?.forEach((state) => {
            if (!relocationOptions.value.find(x => x.name == state.name))
                relocationOptions.value.push({
                    id: state.id,
                    name: state.name,
                    isCity: false,
                });
        }
        );
    }

    const relocationOptions = ref([] as RelocationOption[]);
    const selectedRelocationCity = ref();
    const relocationCityDropDown = ref();

    const computedRelocations = computed(
        () => {
            let result: RelocationOption[];
            result = [
                ...user.value.additionalCities.map((city) => {
                    return { id: city.id, name: city.name, isCity: true };
                }),
                ...user.value.reachableFederalStates.map((state) => {
                    return { id: state.id, name: state.name, isCity: false };
                }),
            ];
            return result;
        }
    );

    const computedIsRemoteOnly = computed(() => {
        return user.value.remote?.indexOf(4) > -1 && user.value.remote?.length == 1
    })


    const hasSelectedCityOrState = computed(() => {
        return (user.value.additionalCities && user.value.additionalCities.length > 0) ||
            (user.value.reachableFederalStates && user.value.reachableFederalStates.length > 0);
    })

    const relocationCitySelected = () => {
        if (!selectedRelocationCity.value)
            return;
        if (selectedRelocationCity.value.isCity) {
            if (!user.value.additionalCities.find(x => x.id == selectedRelocationCity.value.id))
                user.value.additionalCities.push(selectedRelocationCity.value);
        } else {
            if (!user.value.reachableFederalStates.find(x => x.id == selectedRelocationCity.value.id))
                user.value.reachableFederalStates.push(selectedRelocationCity.value);
        }
        relocationCityDropDown.value.filterValue = "";
        selectedRelocationCity.value = "";
    }

    const handleRelocationOptionRemoval = async (
        event,
        option: RelocationOption
    ) => {
        if (option.isCity) {
            user.value.additionalCities =
                user.value.additionalCities.filter(
                    (city) => city.id != option.id
                );
        } else {
            user.value.reachableFederalStates =
                user.value.reachableFederalStates.filter(
                    (state) => state.id != option.id
                );
        }
    };

    const employerTypes = ref(getTechEmployerTypes(t));

    const filteredCompanies = ref<Company[]>();
    let lastSearchTerm = "";

    const handleFilterInput = async (event) => {

        const term = event.query as string;
        lastSearchTerm = term;
        if (term.length < 2) {
            filteredCompanies.value = [];
            return;
        }

        await filterDebounced(term);
    };

    const debounce = (fn, delay) => {
        let timeoutID: null | number;
        return function () {
            if (timeoutID)
                clearTimeout(timeoutID);
            const args = arguments;
            timeoutID = setTimeout(() => {
                fn.apply(this, args);
            }, delay);
        };
    };

    const filterDebounced = debounce(async (term: string) => {
        const service = await createProfileService(app);

        const companies = await service.getFilteredCompanies(term);

        if (lastSearchTerm != term) {
            return;
        }

        filteredCompanies.value = companies
            ? companies.sort((a, b) => a.name.localeCompare(b.name))
            : ([] as Company[]);

        filteredCompanies.value = [{ id: -1, name: term }, ...filteredCompanies.value];

    }, 500);

    const handleCompanyExclusionRemoval = async (event, company: Company) => {
        user.value.excludedCompanies =
            user.value.excludedCompanies.filter(
                (comp) => comp.name != company.name
            );
    };

    const computedCompanies = computed({
        get: () => {
            return user.value.excludedCompanies;

        },
        set: (value) => {
            user.value.excludedCompanies = value;

        },
    });

    // workaround for enums, which have the int value of 0
    const daysChanged = () => {
        nextTick(() => {{
            document.getElementById("days_label")?.classList.add("p-inputwrapper-filled")
        }});
    }

    const rules = computed(() => {
        return {
            remote: { },
            desiredEmployerTypes: { },
            searchRadius: { },
            willingnessToTravelDaysPerWeek: { }

        }
    });
    const v$ = useVuelidate(rules, user);
    const submitted = ref(false);
    const validate = () => {
        submitted.value = true;
        v$.value.$touch();
        return v$.value.$error;
    }

    const next = async () => {
        if (validate())
            return;
        emit("next");
    }
    const prev = async () => {
        emit("previous");
    }

    onMounted(async () => {
        loadSuggestions();
        if (user.value.willingnessToTravelDaysPerWeek == 0)
            user.value.willingnessToTravelDaysPerWeek = null;
    });

</script>
